/**
 * One `notifications` table, two views (#623, #624).
 *
 * A row addressed to a member (`is_for_member`) is a **communication**: it
 * went to a homeowner, and the homeowner reads it in the member portal. A
 * row addressed to a user (`is_for_staff`) is **staff** mail for a
 * management user (today, the manager who issued a citation the homeowner
 * replied to). A row whose recipient was deleted is neither.
 *
 * The management list endpoint returns both, scoped by HOA alone, and
 * pages at 20 by default. Until it can filter by recipient (#625), both
 * views load every page and split here.
 */
import { notificationService } from '../clients/ApiClients';
import { NotificationResponse } from '../clients/rust-api-client';

export const isCommunication = (n: NotificationResponse): boolean =>
  n.is_for_member;

export const isStaffNotification = (n: NotificationResponse): boolean =>
  n.is_for_staff;

const PAGE_SIZE = 100;

/** Every notification this person can see, newest first. */
export const fetchAllNotifications = async (): Promise<
  NotificationResponse[]
> => {
  const all: NotificationResponse[] = [];
  for (let page = 1; ; page++) {
    const { data } = await notificationService.getNotifications({
      page,
      perPage: PAGE_SIZE,
    });
    all.push(...data.notifications);
    if (data.notifications.length < PAGE_SIZE || all.length >= data.total) {
      return all;
    }
  }
};

/**
 * The backend's `NotificationType`. The column holds its `Display` form,
 * PascalCase (`CustomMessage`), not the snake_case of its serde form, so
 * labels match case- and separator-insensitively.
 */
export const NOTIFICATION_TYPES = [
  { value: 'Violation', label: 'Violation' },
  { value: 'Comment', label: 'Comment' },
  { value: 'CustomMessage', label: 'Message' },
  { value: 'Payment', label: 'Payment' },
  { value: 'Maintenance', label: 'Maintenance' },
  { value: 'Announcement', label: 'Announcement' },
  { value: 'Document', label: 'Document' },
  { value: 'Approval', label: 'Approval' },
  { value: 'MemberChange', label: 'Member change' },
  { value: 'Reservation', label: 'Reservation' },
] as const;

const squash = (s: string): string => s.toLowerCase().replace(/[_\s-]/g, '');

export const notificationTypeLabel = (type: string): string =>
  NOTIFICATION_TYPES.find((t) => squash(t.value) === squash(type))?.label ??
  type;

/**
 * The comment a notification is about — a homeowner's reply — when it is
 * about one.
 *
 * Read past the generated type: the API sends `comment_id`, but the client
 * is regenerated only once the backend that added it has deployed. Use the
 * field directly after that regeneration.
 */
export const commentIdOf = (n: NotificationResponse): string | null =>
  (n as NotificationResponse & { comment_id?: string | null }).comment_id ?? null;

const recordPath = (n: NotificationResponse): string | null => {
  if (!n.entity_id) return null;
  switch (n.entity_type) {
    case 'violation':
      return `/violations/${n.entity_id}`;
    case 'work_order':
      return `/work-orders/${n.entity_id}`;
    // A request waiting for approval, a cancellation, a refund due (#642).
    case 'reservation':
      return `/reservations/${n.entity_id}`;
    default:
      return null;
  }
};

/**
 * The front-porch page a notification is about, if it has one. A reply
 * also names its comment, which the page outlines (`?comment=`).
 */
export const entityPath = (n: NotificationResponse): string | null => {
  const path = recordPath(n);
  const commentId = commentIdOf(n);
  return path && commentId
    ? `${path}?comment=${encodeURIComponent(commentId)}`
    : path;
};

/** The HOAs that appear in the rows, by name, for a filter. */
export const hoaOptions = (
  rows: NotificationResponse[]
): { value: string; label: string }[] => {
  const byId = new Map(rows.map((n) => [n.hoa_id, n.hoa_name]));
  return Array.from(byId, ([value, label]) => ({ value, label })).sort((a, b) =>
    a.label.localeCompare(b.label)
  );
};

/** The types that appear in the rows, labelled, for a filter. */
export const typeOptions = (
  rows: NotificationResponse[]
): { value: string; label: string }[] =>
  Array.from(new Set(rows.map((n) => n.notification_type)))
    .map((value) => ({ value, label: notificationTypeLabel(value) }))
    .sort((a, b) => a.label.localeCompare(b.label));
